/**
 * Shared US + CA stock index for Portfolio and Watchlist.
 * Fetches pre-built index from server cacache (/api/stocks/latest_markets_index).
 */

import { getCachedLatestTradeDatesByMarket, getLatestTradeDateKeySync, clearTradeDatesClientCache } from './tradeDatesCache';
import {
  clearStocksByDateMemoryCache,
  fetchStocksIndexForLatestMarkets,
  resolveStockForSymbol,
  resolveMarketForStock,
} from './stocksByMarketDate';
import { serializeLatestByMarket } from './marketTradeDates';
import { authFetch } from './authFetch';

const PW_STOCK_INDEX_CACHE_KEY = 'pwStockIndexCache';
const PW_STOCK_INDEX_CACHE_TTL = 60 * 60 * 1000; // 1 hour

const inFlightIndex = { promise: null, timestamp: null };

const safeAverage = (arr) => {
  const valid = (arr || []).filter((val) => !Number.isNaN(val));
  return valid.length === 0 ? 0 : valid.reduce((a, b) => a + b, 0) / valid.length;
};

const mapFromCacheObject = (obj) => new Map(Object.entries(obj || {}));

const mapToCacheObject = (map) => {
  const out = {};
  if (!map) return out;
  for (const [key, value] of map.entries()) {
    out[key] = value;
  }
  return out;
};

export const clearPwStockIndexCache = () => {
  try {
    localStorage.removeItem(PW_STOCK_INDEX_CACHE_KEY);
  } catch { /* ignore */ }
  inFlightIndex.promise = null;
  inFlightIndex.timestamp = null;
  console.log('[PW STOCK INDEX] Cleared shared US/CA stock index cache');
};

export const clearPwStockIndexCacheAndStocksMemory = () => {
  clearPwStockIndexCache();
  clearStocksByDateMemoryCache();
};

const readCachedPwStockIndex = (tradeDateKey) => {
  if (!tradeDateKey) return null;

  try {
    const raw = localStorage.getItem(PW_STOCK_INDEX_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const age = Date.now() - (parsed.timestamp || 0);

    if (age >= PW_STOCK_INDEX_CACHE_TTL) {
      localStorage.removeItem(PW_STOCK_INDEX_CACHE_KEY);
      return null;
    }

    if (parsed.tradeDateKey !== tradeDateKey) {
      return null;
    }

    const stocksBySymbolId = mapFromCacheObject(parsed.stocksBySymbolId);
    if (stocksBySymbolId.size === 0) return null;

    console.log(
      `[PW STOCK INDEX] Using client cache (age: ${Math.round(age / 1000)}s, tradeDateKey: ${tradeDateKey}, ${stocksBySymbolId.size} symbols)`
    );

    return {
      stocksBySymbolId,
      tradeDateKey: parsed.tradeDateKey,
      latestByMarket: parsed.latestByMarket || {},
      datesUsed: parsed.datesUsed || [],
    };
  } catch (error) {
    console.warn('[PW STOCK INDEX] Error reading client cache:', error);
    try { localStorage.removeItem(PW_STOCK_INDEX_CACHE_KEY); } catch { /* ignore */ }
    return null;
  }
};

const writeCachedPwStockIndex = (indexResult) => {
  if (!indexResult?.tradeDateKey || !indexResult.stocksBySymbolId?.size) return;

  try {
    localStorage.setItem(
      PW_STOCK_INDEX_CACHE_KEY,
      JSON.stringify({
        tradeDateKey: indexResult.tradeDateKey,
        latestByMarket: indexResult.latestByMarket,
        datesUsed: indexResult.datesUsed,
        stocksBySymbolId: mapToCacheObject(indexResult.stocksBySymbolId),
        timestamp: Date.now(),
      })
    );
    console.log(
      `[PW STOCK INDEX] Cached index locally (${indexResult.stocksBySymbolId.size} symbols, tradeDateKey: ${indexResult.tradeDateKey})`
    );
  } catch (error) {
    console.warn('[PW STOCK INDEX] Error writing client cache:', error);
  }
};

const normalizeServerIndexResponse = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid latest markets stock index response');
  }

  const stocksBySymbolId = mapFromCacheObject(data.stocksBySymbolId);
  return {
    stocksBySymbolId,
    tradeDateKey: data.tradeDateKey || '',
    latestByMarket: data.latestByMarket || {},
    datesUsed: Array.isArray(data.datesUsed) ? data.datesUsed : [],
  };
};

const fetchLatestMarketsStockIndexFromServer = async (forceRefresh = false) => {
  const url = forceRefresh
    ? '/api/stocks/latest_markets_index?refresh=1'
    : '/api/stocks/latest_markets_index';

  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch latest markets stock index: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return normalizeServerIndexResponse(data);
};

/**
 * Load merged US + CA stock index at each market's latest trade date.
 * pwStockIndexCache (sync) → coalesced per-date fetches → server cacache fallback.
 * @param {{ forceRefresh?: boolean }} options
 * @returns {Promise<{ stocksBySymbolId: Map, tradeDateKey: string, latestByMarket: object, datesUsed: string[] }|null>}
 */
export const getLatestMarketsStockIndex = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const tradeDateKey = getLatestTradeDateKeySync();
    if (tradeDateKey) {
      const cached = readCachedPwStockIndex(tradeDateKey);
      if (cached) return cached;
    }
  } else {
    clearPwStockIndexCacheAndStocksMemory();
  }

  const now = Date.now();
  if (inFlightIndex.promise && now - (inFlightIndex.timestamp || 0) < 15000) {
    console.log('[PW STOCK INDEX] Waiting for in-flight index build');
    return inFlightIndex.promise;
  }

  const fetchPromise = (async () => {
    const latestByMarket = await getCachedLatestTradeDatesByMarket();
    if (!latestByMarket?.US && !latestByMarket?.CA) {
      console.warn('[PW STOCK INDEX] No latest trade dates by market');
      return null;
    }

    console.log(
      `[PW STOCK INDEX] Building index for US=${latestByMarket.US || 'n/a'}, CA=${latestByMarket.CA || 'n/a'}`
    );

    let result = await fetchStocksIndexForLatestMarkets(latestByMarket, {
      forceRefresh,
    });

    if (!result.stocksBySymbolId?.size) {
      console.log('[PW STOCK INDEX] Client index empty, trying server cache');
      try {
        result = await fetchLatestMarketsStockIndexFromServer(forceRefresh);
      } catch (error) {
        console.warn('[PW STOCK INDEX] Server index fetch failed:', error);
        return result;
      }
    }

    if (!result.stocksBySymbolId?.size) {
      console.warn('[PW STOCK INDEX] No stocks indexed for latest US/CA dates');
      return {
        ...result,
        latestByMarket: result.latestByMarket || latestByMarket,
      };
    }

    writeCachedPwStockIndex(result);
    return result;
  })();

  inFlightIndex.promise = fetchPromise;
  inFlightIndex.timestamp = now;

  try {
    return await fetchPromise;
  } finally {
    inFlightIndex.promise = null;
    inFlightIndex.timestamp = null;
  }
};

/**
 * @param {Record<string, Array>} portfolio - DynamoDB portfolio map for one list
 * @param {{ stocksBySymbolId: Map, latestByMarket: object }} indexResult
 * @returns {object[]}
 */
export const mergePortfolioStockRows = (portfolio, indexResult) => {
  if (!portfolio || !indexResult) return [];

  const { stocksBySymbolId, latestByMarket } = indexResult;
  const mergedStocks = [];

  for (const compositeKey of Object.keys(portfolio)) {
    const pipeIdx = compositeKey.indexOf('|');
    const symbolId = pipeIdx !== -1 ? compositeKey.slice(0, pipeIdx) : compositeKey;

    const stockData = resolveStockForSymbol(symbolId, stocksBySymbolId, latestByMarket);
    if (!stockData) continue;

    const symbolEntry = portfolio[compositeKey];
    if (!Array.isArray(symbolEntry)) continue;

    const [buyPrice, buyDate, addDate, rawQty, rawActionType] = symbolEntry;
    const parsedBuyPrice = parseFloat(buyPrice);
    const qty = parseInt(rawQty, 10);
    const actionTypeVal = rawActionType;

    const performance =
      parsedBuyPrice > 0
        ? actionTypeVal === 'short'
          ? (100 * (parsedBuyPrice - stockData.close)) / parsedBuyPrice
          : (100 * (stockData.close - parsedBuyPrice)) / parsedBuyPrice
        : 0;

    const closePrice = stockData.close || 0;
    const cost = actionTypeVal === 'short' ? closePrice * qty : parsedBuyPrice * qty;
    const marketValue = actionTypeVal === 'short' ? parsedBuyPrice * qty : closePrice * qty;

    mergedStocks.push({
      composite_key: compositeKey,
      symbol_id: stockData.symbol_id,
      symbol: stockData.symbol,
      name: stockData.name,
      sector: stockData.sector || '--',
      instrument: stockData.instrument || '--',
      currency: stockData.currency || 'USD',
      market: resolveMarketForStock(stockData),
      trade_date: stockData.trade_date,
      trade_timestamp: stockData.trade_timestamp,
      close: stockData.close,
      days_change: stockData.pct_change,
      market_cap: stockData.market_cap,
      status: stockData.status1,
      pct_change_5days: stockData.pct_change_5days,
      pct_change_20days: stockData.pct_change_20days,
      buy_price: parsedBuyPrice,
      buy_date: buyDate,
      updated_at: addDate,
      quantity: qty,
      action_type: actionTypeVal,
      cost,
      market_value: marketValue,
      performance,
    });
  }

  return mergedStocks;
};

/** @param {object[]} mergedStocks */
export const computePortfolioSummary = (mergedStocks) => {
  const totalCost = mergedStocks.reduce((sum, s) => sum + (s.cost || 0), 0);
  const totalMarketValue = mergedStocks.reduce((sum, s) => sum + (s.market_value || 0), 0);

  return {
    avg1WeekPerformance: safeAverage(mergedStocks.map((s) => s.pct_change_5days || 0)),
    avg4WeekPerformance: safeAverage(mergedStocks.map((s) => s.pct_change_20days || 0)),
    avgReturns: safeAverage(mergedStocks.map((s) => s.performance)),
    avgPercentGain: totalCost > 0 ? (100 * (totalMarketValue - totalCost)) / totalCost : 0,
    totalCost,
    totalMarketValue,
  };
};

/**
 * @param {string[]} symbolIds
 * @param {{ stocksBySymbolId: Map, latestByMarket: object }} indexResult
 * @returns {object[]}
 */
export const mergeWatchlistStockRows = (symbolIds, indexResult) => {
  if (!Array.isArray(symbolIds) || !indexResult) return [];

  const { stocksBySymbolId, latestByMarket } = indexResult;

  return symbolIds
    .map((symbolId) => resolveStockForSymbol(symbolId, stocksBySymbolId, latestByMarket))
    .filter(Boolean);
};

/** @param {object[]} stocks */
export const computeWatchlistSummary = (stocks) => ({
  avg6MonthPerformance: safeAverage(stocks.map((s) => s.pct_change_120days || 0)),
  avg1WeekPerformance: safeAverage(stocks.map((s) => s.pct_change_5days || 0)),
  avg1MonthPerformance: safeAverage(stocks.map((s) => s.pct_change_20days || 0)),
  avg3MonthPerformance: safeAverage(stocks.map((s) => s.pct_change_60days || 0)),
});

/**
 * Full pipeline: US + CA latest index → portfolio display rows + summary.
 * @param {Record<string, Array>} portfolio
 * @param {{ forceRefresh?: boolean, _retried?: boolean }} options
 */
export const buildPortfolioDisplayData = async (portfolio, options = {}) => {
  const indexResult = await getLatestMarketsStockIndex(options);
  if (!indexResult) return null;

  const portfolioStocks = mergePortfolioStockRows(portfolio, indexResult);
  const portfolioKeys = Object.keys(portfolio || {});

  if (
    !options._retried &&
    portfolioKeys.length > 0 &&
    portfolioStocks.length < portfolioKeys.length
  ) {
    console.warn(
      `[PW STOCK INDEX] Portfolio missing ${portfolioKeys.length - portfolioStocks.length}/${portfolioKeys.length} symbols — refreshing per-market trade dates`
    );
    clearTradeDatesClientCache();
    clearPwStockIndexCacheAndStocksMemory();
    return buildPortfolioDisplayData(portfolio, { forceRefresh: true, _retried: true });
  }

  const portfolioSummary = computePortfolioSummary(portfolioStocks);

  return {
    portfolioStocks,
    portfolioSummary,
    tradeDateKey: indexResult.tradeDateKey,
    latestByMarket: indexResult.latestByMarket,
    datesUsed: indexResult.datesUsed,
  };
};

/**
 * Full pipeline: US + CA latest index → watchlist display rows + summary.
 * @param {string[]} symbolIds
 * @param {{ forceRefresh?: boolean, _retried?: boolean }} options
 */
export const buildWatchlistDisplayData = async (symbolIds, options = {}) => {
  const indexResult = await getLatestMarketsStockIndex(options);
  if (!indexResult) return null;

  const watchlistStocks = mergeWatchlistStockRows(symbolIds, indexResult);

  if (
    !options._retried &&
    Array.isArray(symbolIds) &&
    symbolIds.length > 0 &&
    watchlistStocks.length < symbolIds.length
  ) {
    console.warn(
      `[PW STOCK INDEX] Watchlist missing ${symbolIds.length - watchlistStocks.length}/${symbolIds.length} symbols — refreshing per-market trade dates`
    );
    clearTradeDatesClientCache();
    clearPwStockIndexCacheAndStocksMemory();
    return buildWatchlistDisplayData(symbolIds, { forceRefresh: true, _retried: true });
  }

  const watchlistSummary = computeWatchlistSummary(watchlistStocks);

  return {
    watchlistStocks,
    watchlistSummary,
    tradeDateKey: indexResult.tradeDateKey,
    latestByMarket: indexResult.latestByMarket,
    datesUsed: indexResult.datesUsed,
  };
};
